<template>
  <div class="outer-box max-w-xl">
    <h4>Bulk Suspend Twilio Accounts</h4>

    <b-alert show variant="warning" class="text-left m-3">
      Warning: This action will suspend Twilio subaccounts and automatically close them after 14 days. Use with caution. Only authorized personnel can perform this action.
    </b-alert>

    <div class="inner-box">
      <div class="form-group">
        <label class="form-label">Enter Location ID or bulk upload a csv</label>
        <div class="d-flex align-items-center">
          <input
            id="locationId"
            v-model="singleLocationId"
            class="hl_input--wrap mr-2"
            type="text"
            placeholder="Enter Location ID"
            :disabled="isCsvSelected"
          />
          <input
            ref="csvFileInput"
            type="file"
            @change="handleFileUpload"
            accept=".csv"
            style="display: none"
          />
          <button class="btn btn-dark" @click.prevent="triggerFileSelect">Upload a csv</button>
        </div>
        <div v-if="selectedFile" class="mt-2">
          <strong>Selected file:</strong> {{ selectedFile.name }}
        </div>
        <small class="form-text text-muted mt-2">
          * csv should have columns: <code>locationid</code> and <code>reason</code>
        </small>
      </div>

      <div class="form-group">
        <label for="reason" class="form-label">Suspension Reason (required)</label>
        <textarea
          id="reason"
          v-model="reason"
          class="hl_input--wrap"
          rows="3"
          :placeholder="isCsvSelected ? 'Reasons are in the CSV file' : 'Enter reason for suspension (required)'"
          :disabled="isCsvSelected"
          :class="{ 'disabled-textarea': isCsvSelected }"
        ></textarea>
        <small class="form-text text-muted mt-2">*the above reason will be communicated to the agency owners.</small>
      </div>

      <button
        class="btn btn-danger mt-4"
        @click="showConfirmationModal"
        :disabled="!canSubmit || isProcessing"
      >
        <moon-loader v-if="isProcessing" :loading="true" size="21px" color="#fafafa" />
        <span v-else>{{ isCsvSelected ? 'Suspend Accounts (CSV)' : 'Suspend Single Account' }}</span>
      </button>

      <pre v-if="results" class="bg-f2f7fa p-16 mt-16">{{ results }}</pre>
      
      <div v-if="error" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ error }}
      </div>
    </div>

    <b-modal
      id="confirmSuspensionModal"
      ref="confirmModal"
      title="Confirm Suspension"
      ok-variant="danger"
      ok-title="Yes, Suspend Accounts"
      cancel-title="Cancel"
      @ok="performSuspension"
    >
      <div class="mb-3">
        <strong>⚠️ You are about to suspend {{ getAccountCount() }} Twilio account(s).</strong>
      </div>
      <div class="mb-3">
        <strong>Reason:</strong> {{ isCsvSelected ? "See CSV for reasons" : reason }}
      </div>
      <div class="mb-3">
        <strong>Warning:</strong> Suspended accounts will be automatically closed after 14 days. 
        This action cannot be easily undone.
      </div>
      <div>
        Are you sure you want to proceed?
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ISVService } from "@/services";

export default {
  name: "BulkSuspendTwilioAccounts",
  data() {
    return {
      singleLocationId: "",
      selectedFile: null,
      reason: "",
      isProcessing: false,
      results: null,
      error: null,
    };
  },
  computed: {
    canSubmit() {
      if (this.isCsvSelected) {
        return true;
      }
      const hasLocation = this.singleLocationId.trim();
      const hasReason = this.reason.trim();
      return hasLocation && hasReason;
    },
    isCsvSelected() {
      return !!this.selectedFile;
    },
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.singleLocationId = "";
        this.reason = "";
        this.clearResults();
      } else {
        this.selectedFile = null;
      }
    },
    triggerFileSelect() {
      this.$refs.csvFileInput.click();
    },
    getAccountCount() {
      if (this.singleLocationId.trim()) {
        return 1;
      } else if (this.selectedFile) {
        return "multiple";
      }
      return 0;
    },
    showConfirmationModal() {
      if (!this.canSubmit) {
        this.error = "Please provide either a location ID or CSV file, and a reason for suspension.";
        return;
      }
      this.$refs.confirmModal.show();
    },
    async performSuspension() {
      this.isProcessing = true;
      this.clearResults();
      try {
        let response;
        if (this.isCsvSelected) {
          const formData = new FormData();
          formData.append('file', this.selectedFile, this.selectedFile.name);
          response = await ISVService.bulkSuspendTwilioAccounts(formData);
        } else {
          response = await ISVService.suspendTwilioAccount({
            locationId: this.singleLocationId.trim(),
            reason: this.reason.trim(),
          });
        }
        this.results = response.data;
        this.clearForm();
      } catch (error) {
        if (error.response && error.response.data) {
          this.error = error.response.data.message || JSON.stringify(error.response.data);
        } else {
          this.error = error.message || "An unknown error occurred during bulk suspension.";
        }
      } finally {
        this.isProcessing = false;
      }
    },
    clearForm() {
      this.singleLocationId = "";
      this.selectedFile = null;
      this.reason = "";
      this.$refs.csvFileInput.value = null;
    },
    clearResults() {
      this.results = null;
      this.error = null;
    },
  },
};
</script>

<style scoped>
.inner-box {
  text-align: left;
  width: 500px;
}
.form-label {
  font-weight: 600;
  margin-bottom: 8px;
  color: #495057;
  display: block;
}
.form-group {
  margin-bottom: 1.5rem;
}
.btn-dark {
  background-color: #3a3a3a;
  color: #fff;
}
.disabled-textarea {
  background-color: #e9ecef;
  opacity: 1;
}
</style>